import React from 'react';
import { Building2, AlertTriangle } from 'lucide-react';
import { MARK } from '../../constants';

const SELO = {
  'sem-contrato': { texto: 'Sem comercial', marca: MARK.WARN },
  inativo:        { texto: 'Inativo',       marca: MARK.BAD },
  vencido:        { texto: 'Vencido',       marca: MARK.BAD },
  vencendo:       { texto: 'Vence em breve', marca: MARK.PENDING },
  ativo:          { texto: 'Ativo',         marca: '' },
};

const TIER = {
  interno: 'Interno', piloto: 'Piloto', essencial: 'Essencial', pro: 'Pro',
};

/** "há 3 dias" diz mais que a data quando a pergunta é "ainda estão usando?". */
const desde = (iso) => {
  if (!iso) return 'nunca';
  const dias = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  const meses = Math.floor(dias / 30);
  return meses === 1 ? 'há 1 mês' : `há ${meses} meses`;
};

/**
 * Lista de laboratórios, ordenada por atenção.
 *
 * As colunas respondem, em ordem, o que se pergunta olhando isto uma vez por
 * semana: quem está com problema comercial, quanto cada um consome, e quem
 * parou de lançar. `montarLinhas` já entrega ordenado.
 */
const ListaLaboratorios = ({
  linhas, selecionadoId, onSelecionar, usoMensalPorLab = {}, semAdminPorLab = {},
}) => (
  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Laboratório</th>
            <th className="px-3 py-2 text-left font-medium">Plano</th>
            <th className="px-3 py-2 text-left font-medium">Situação</th>
            <th className="px-3 py-2 text-center font-medium">Clientes</th>
            <th className="px-3 py-2 text-center font-medium">Serviços</th>
            <th className="px-3 py-2 text-center font-medium">Embriões</th>
            <th className="px-3 py-2 text-center font-medium">Sessões no mês</th>
            <th className="px-3 py-2 text-left font-medium">Último lançamento</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {linhas.map(l => {
            const selo      = SELO[l.situacao];
            const arquivado = l.status === 'archived';
            const mes       = usoMensalPorLab[l.id];
            const franquia  = l.contrato?.franquia_sessoes_mes;
            const estourou  = franquia && mes != null && mes > franquia;

            return (
              <tr key={l.id}
                onClick={() => onSelecionar(l.id)}
                className={`cursor-pointer hover:bg-slate-50 ${
                  selecionadoId === l.id ? 'bg-blue-50' : ''} ${
                  arquivado ? 'text-slate-400' : ''}`}>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-slate-400 shrink-0" />
                    <span className={arquivado ? '' : 'font-medium text-slate-900'}>{l.name}</span>
                    {arquivado && (
                      <span className="text-xs border border-slate-200 rounded px-1.5 py-0.5">
                        arquivado
                      </span>
                    )}
                    {/* Onboarding que parou no meio: o laboratório existe e
                        ninguém consegue entrar nele. Precisa saltar aos olhos. */}
                    {semAdminPorLab[l.id] && (
                      <span className="text-xs whitespace-nowrap" title="Ninguém consegue entrar neste laboratório">
                        {MARK.WARN} sem administrador
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {l.contrato?.tier ? TIER[l.contrato.tier] : '—'}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {selo.marca && <span>{selo.marca} </span>}
                  <span className={l.situacao === 'ativo' ? 'text-slate-500' : 'text-slate-900 font-medium'}>
                    {selo.texto}
                  </span>
                  {l.contrato?.expires_at && (
                    <span className="text-slate-400 text-xs block">
                      até {new Date(`${l.contrato.expires_at}T00:00:00`).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">{l.clientes}</td>
                <td className="px-3 py-2 text-center">{l.servicos}</td>
                <td className="px-3 py-2 text-center">{l.embrioes}</td>
                <td className="px-3 py-2 text-center">
                  {mes == null ? '—' : (
                    <span className={estourou ? 'font-semibold text-slate-900' : ''}>
                      {mes}{franquia ? ` / ${franquia}` : ''}
                      {estourou && <span title="Acima da franquia — gera excedente"> {MARK.WARN}</span>}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                  {desde(l.ultimo_lancamento_em)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    {linhas.length === 0 && (
      <div className="px-6 py-10 text-center text-slate-500 text-sm">
        <AlertTriangle className="mx-auto mb-2 text-slate-400" size={28} />
        Nenhum laboratório visível nesta conta.
      </div>
    )}
  </div>
);

export default ListaLaboratorios;
